import React, {
  FC, useEffect, useRef, useState,
} from 'react';
import Tooltip from '@mui/material/Tooltip';
import { useTranslation } from 'react-i18next';
import LeftAngle from '../../assets/icons/left-angle.svg';
import RightAngle from '../../assets/icons/right-angle.svg';
import LoadingSpinner from '../loadingSpinner/loadingSpinner';
import InfoIcon from '../../assets/icons/info-icon.svg';
import EmptyBigCard from '../dashboard/dashboardCards/emptyBigCard';
import { useDashboardFilterDateStore } from '../../store/dateRangeStore';
import AdsReportTable from '../../partials/adsReportTable';
import { TableColumnInterface } from '../../interfaces/tableColumn.interface';
import VatDisplayer from '../../partials/VATDisplayer';
import { useGetKeywordPerformance } from '../../apiHooks/queries/keywordPerformance/useGetKeywordPerformance';
import { useGetExportKeywordTable } from '../../apiHooks/mutations/reportsExportMutations/useGetExportKeywordTable';
import ExportButton from '../exportButton/exportButton';
import SearchIcon from '../../assets/icons/searchIconAds.png';
import { useDebounce } from '../../constants/hookConstants';
import {useCustomFieldsStore} from "../../store/customFieldsStore";

export const keywordColumn: TableColumnInterface[] = [
  {
    field: 'formSubmissions',
    headerName: 'formSubmissions',
    cellRenderer: (params) => <VatDisplayer value={params} />,
  },
  {
    field: 'income',
    headerName: 'income',
    cellRenderer: (params) => <VatDisplayer value={params} />,
  },
  {
    field: 'sales',
    headerName: 'sales',
    cellRenderer: (params) => <VatDisplayer value={params} />,
  },

  {
    field: 'cvRate',
    headerName: 'cvRate',
    cellRenderer: (params, rowObject) => (
      <span>
        {rowObject?.sales && rowObject?.formSubmissions && (
          Math.round((rowObject.sales / (rowObject.formSubmissions || 1)) * 100)
        )}
        %
      </span>
    ),
  },
];

export const referrerReportFixedColumns: TableColumnInterface[] = [
  {
    field: 'keyword',
    headerName: 'keyword',
    cellRenderer: (params: string) => (
      <Tooltip
        arrow
        placement="top"
        title={params}
      >
        <span className="2xl: w-[250px] max-w-[190px] truncate">
          {params === '' ? 'Direct' : params}
        </span>
      </Tooltip>
    ),
  },
];

interface KeywordInterfaceProps {
  adCode?: string;
}

const KeywordPerformance: FC<KeywordInterfaceProps> = (props) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const dashboardFilterDateRange = useDashboardFilterDateStore((state) => state.dashboardFilterDateRange);
  const { selectedCustomField } = useCustomFieldsStore();

  const searchRef = useRef<HTMLDivElement>(null);

  const [showSearch, setShowSearch] = useState(false);

  const handleClickOutside = (event: MouseEvent) => {
    if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
      setShowSearch(false);
    }
  };

  useEffect(() => {
    if (showSearch) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSearch]);

  const toggleSearch = () => {
    setShowSearch((prev) => !prev);
  };

  const {
    data: referrerReport,
    isFetching,
    isError,
  } = useGetKeywordPerformance(
    'KEYWORD_PERFORMANCE',
    dashboardFilterDateRange[0],
    dashboardFilterDateRange[1],
    page || 0,
    props.adCode,
    search,
    selectedCustomField?.id,
    selectedCustomField?.name,
  );

  const { mutate: downloadKeyword } = useGetExportKeywordTable(
    {
      from: dashboardFilterDateRange[0],
      to: dashboardFilterDateRange[1],
      page: page || 0,
    },
  );

  const reportData = referrerReport?.data?.data?.report;

  const handlePrevPage = () => {
    setPage(page - 1);
  };

  const handleNextPage = () => {
    setPage(page + 1);
  };

  if (referrerReport?.data?.data?.report === null && page > 0) {
    setPage(0);
  }
  const debouncedSearch = useDebounce((val: string) => {
    setSearch(val);
  }, 500);

  return (
    <div className="flex h-full flex-1 flex-col text-clip rounded-lg">
      <div
        className="flex items-center justify-between rounded-t-lg border-b bg-white px-3 pb-1 pt-2"
      >
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-medium leading-[36px] text-primary">
            {t('keywordPerformance')}
          </h2>
          <Tooltip title={'Tracks the performance of keywords from search ads.'}>
            <img src={InfoIcon} alt="" className="max-w-[20px]" />
          </Tooltip>
        </div>
        <div className="flex items-center">
          <div className="mx-4 flex w-full items-center justify-end gap-2">
            {selectedCustomField ? <div className={"text-xs text-[#828282]"}>Filtered by Custom Fields</div> : <div />}
            <div
              className="border-gray-300 relative flex items-center rounded border transition-all duration-500 ease-in-out"
            >
              <button
                onClick={toggleSearch}
                disabled={!reportData || reportData.length === 0 || isFetching || isError}
                className={`flex h-8 w-8 items-center justify-center rounded transition-all duration-500 ease-in-out hover:bg-gray-200 ${
                  showSearch ? 'invisible w-0' : 'visible w-8'
                } ${!reportData || reportData.length === 0 || isFetching || isError ? 'bg-[#EAEAEA] hover:cursor-not-allowed' : ''}`}
              >
                <img src={SearchIcon} alt="Search Icon" className="h-4 w-4" />
              </button>
              <div
                ref={searchRef}
                className={`outline- flex items-center overflow-hidden transition-all duration-500 ease-in-out ${
                  showSearch ? 'w-[150px] px-2' : 'w-0 px-0'
                }`}
              >
                <img
                  onClick={reportData ? toggleSearch : () => null}
                  src={SearchIcon}
                  alt="Search"
                  className={`absolute left-2 h-4 w-4 text-[#828282] ${!reportData || reportData.length === 0 || isFetching || isError ? 'hover:cursor-not-allowed' : ' hover:cursor-pointer'}`}
                />
                <input
                  type="text"
                  className={`h-8 w-full rounded border-none py-1 pl-1 pr-2 text-primary outline-none placeholder:text-gray-400 ${
                    showSearch ? 'visible' : 'invisible'
                  }`}
                  placeholder={`${t('Search') || 'Search'}`}
                  onChange={(e) => debouncedSearch(e.target.value)}
                />
              </div>
            </div>
          </div>
          <div className="flex w-[220px] justify-center rounded-[6px] border py-1">
            <ExportButton fetchDownload={downloadKeyword} />
          </div>
        </div>
      </div>
      <div className="flex h-[499px] w-full flex-col items-center  text-center">
        {!isFetching && !isError && (reportData === null || reportData?.length === 0) && (
        <div className="flex h-full object-contain">
          <EmptyBigCard />
        </div>
        )}
        {isFetching && <LoadingSpinner center color="primary" />}
        {isError && !isFetching && (
        <p>
          {t('errorRetrievingData')}
        </p>
        )}
        {!isFetching && !isError && reportData !== null && reportData && reportData?.length > 0 && (
        <AdsReportTable
          data={referrerReport?.data?.data?.report}
          isFetching={isFetching}
          isError={isError}
          columns={keywordColumn}
          fixedColumns={referrerReportFixedColumns}
        />
        )}
      </div>
      {!isFetching && !isError && reportData !== null && reportData && reportData?.length > 0 && (
        <div className="mb-[22px] mt-3 flex items-center justify-center gap-4">
          <button
            type="button"
            onClick={handlePrevPage}
            className={`
              ${page === 0
              ? 'cursor-default opacity-30'
              : 'cursor-pointer hover:bg-primary/20 active:bg-primary/30'}
               grid h-[28px] w-[28px] place-content-center  rounded-lg bg-primary/10 
            `}
            disabled={page === 0 || isFetching}
          >
            <img src={LeftAngle} alt="" />
          </button>
          <button
            type="button"
            onClick={handleNextPage}
            className={`
              ${!referrerReport?.data?.data?.hasNextPage
              ? 'cursor-default opacity-30'
              : 'cursor-pointer hover:bg-primary/20 active:bg-primary/30'}
               grid h-[28px] w-[28px] place-content-center rounded-lg bg-primary/10
            `}
            disabled={!referrerReport?.data?.data?.hasNextPage || isFetching}
          >
            <img src={RightAngle} alt="" />
          </button>
        </div>
      )}
    </div>
  );
};
export default KeywordPerformance;
